import React, { useCallback, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useImFacilitator from './useImFacilitator';
import * as S from './style';
import { useAddActionItemMutation } from './hooks';
import { logEvent, TrackingEvents } from '../../tracking';
import { castingWorkItems } from '../../utils/helpers';
import { ActionItemType } from '../../DomainEnums';
import { createItem, openItemDialog } from '../../services/items';
import { GlobalContext } from '../../context/GlobalState';
import { ActionItemView, AddActionItem } from '../../components';
import { SelectedOption } from '../../components/AddActionItem/AddActionItem';
import { MONDAY_ITEM } from '../../components/DropDownNew';
import { Card, ItemTypes } from '../../@types/domain';
import { HostPlatform } from 'src/config/hostPlatform';

const VotedCard = ({
  card,
  itemTypes,
  isFocused,
}: {
  card: Card;
  itemTypes: ItemTypes[];
  isFocused: boolean;
}) => {
  const [isAddingAction, setIsAddingAction] = useState<boolean>(false);
  const { cards, retrospective } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  const isFacilitator = useImFacilitator();

  const { mutateAsync: addActionItem, isLoading } = useAddActionItemMutation(
    card.id
  );

  const handleAddItem = useCallback(
    async ({ type, value: newActionItemName }: SelectedOption) => {
      setIsAddingAction(true);
      try {
        const currentCard = cards.find((item) => item.id === card.id);
        if (!newActionItemName || !currentCard) {
          return;
        }

        logEvent(TrackingEvents.ADD_RETROSPECTIVE_ACTION);

        if (type.name === ActionItemType.TEXT_NOTE) {
          const item = castingWorkItems(
            currentCard,
            newActionItemName,
            type.name
          );
          return addActionItem(item);
        }

        const createdPlatformItem = await createItem(
          retrospective?.boardId,
          newActionItemName,
          type,
          retrospective
        );
        const item = castingWorkItems(
          currentCard,
          newActionItemName,
          type.name,
          createdPlatformItem.id
        );
        return addActionItem(item);
      } finally {
        setIsAddingAction(false);
      }
    },
    [addActionItem, card.id, cards, retrospective]
  );

  const handleEditItemClick = async ({ id }) => {
    await openItemDialog(id);
    return queryClient.invalidateQueries(['getPlatformItemsByIds']);
  };

  return (
    <S.VotedCard>
      <S.CardContent>
        <S.VotedCount>
          <span>{card.votes.length}</span>
          <small>{card.votes.length === 1 ? 'Vote' : 'Votes'}</small>
        </S.VotedCount>

        <S.CardDescription>
          <S.BadgeColumn>{card.columnName}</S.BadgeColumn>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{card.description}</pre>
        </S.CardDescription>
      </S.CardContent>

      <S.ActivityList>
        {card.actionItems.map((actionItem, actionItemIndex) => {
          return (
            <ActionItemView
              id={
                HostPlatform.isJira()
                  ? (actionItem.key as string)
                  : actionItem.id
              }
              key={
                card.id + (actionItem.platformActionItemId || actionItem.text)
              }
              allowEdit={isFacilitator}
              allowRemove={isFacilitator}
              card={card}
              item={actionItem}
              index={actionItemIndex}
              handleClick={() => handleEditItemClick(actionItem)}
            />
          );
        })}

        {isFacilitator && (
          <AddActionItem
            key={card.id}
            actionItems={card.actionItems}
            addItem={handleAddItem}
            options={itemTypes.length > 0 ? itemTypes : MONDAY_ITEM}
            disabled={isLoading || isAddingAction}
            isFocused={isFocused}
          />
        )}
      </S.ActivityList>
    </S.VotedCard>
  );
};

export default VotedCard;
